import { ContentType } from '@/models/Session';

import { computed, ref, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';

import { getGhccSsoUrl } from '@/utils/ghcc';

import { useUserStore } from './useUserStore';
import { useSessionStore } from './useSessionStore';
import { useContentStore } from './useContentStore';

export const useGhccSessionStore = defineStore('ghccSession', () => {
  const sessionStore = useSessionStore();

  const { user } = storeToRefs(useUserStore());
  const { isInitialized } = storeToRefs(useContentStore());

  const isConnecting = ref(false);
  const sessionId = ref<string>();
  const ssoUrl = ref<string>();
  const passportId = ref<string>();
  const linkSpeed = ref<number>(1280);

  const isReadyToConnect = computed(
    () => user.value && sessionStore.contentType === ContentType.course && isInitialized.value,
  );

  const isConnected = computed(() => Boolean(sessionId.value) && Boolean(passportId.value));

  const setLinkSpeed = (newValue: number) => {
    if (newValue > linkSpeed.value) linkSpeed.value = newValue;
  };

  const setPassportId = (value: string) => {
    passportId.value = value;
  };

  const connectToGhcc = async () => {
    if (!isReadyToConnect.value || !user.value || isConnecting.value || isConnected.value) {
      return;
    }

    isConnecting.value = true;

    sessionId.value = `${user.value.Id}_${Math.floor(Date.now() / 1000)}`;

    try {
      const url = await getGhccSsoUrl((sessionStore.contentId as number) ?? 0, sessionId.value);
      if (url) {
        ssoUrl.value = `${url}&EPR=${user.value.ExternalPersonReference}&ECR=${user.value.ExternalCompanyReference}`;
      }
    } catch {
      throw new Error('Could not connect to GHCC');
    } finally {
      isConnecting.value = false;
    }
  };

  watch(
    isReadyToConnect,
    (isReady) => {
      if (!isReady) return;

      connectToGhcc();
    },
    { immediate: true },
  );

  return {
    isConnecting,
    isConnected,
    linkSpeed,
    passportId,
    sessionId,
    ssoUrl,
    setLinkSpeed,
    setPassportId,
    connectToGhcc,
  };
});
